import type { ReactNode } from "react";
import { useAuthStore } from "../../stores/authStore";

interface ModuleGateProps {
  /**
   * The module code required for access (e.g., "INVENTORY", "REPORTS_ADVANCED")
   */
  requiredModule: string;
  /**
   * Content to render if user has access to the module
   */
  children: ReactNode;
  /**
   * Optional content to render if user doesn't have access
   * If not provided, nothing is rendered when access is denied
   */
  fallback?: ReactNode;
  /**
   * Content to render while modules are loading
   * If not provided, nothing is rendered during loading
   */
  loading?: ReactNode;
}

/**
 * ModuleGate - Conditionally renders children based on module subscription
 *
 * Use this component to show/hide UI elements based on whether the tenant
 * has access to a specific module in their subscription plan.
 *
 * @example
 * // Hide button if module not enabled
 * <ModuleGate requiredModule="REPORTS_ADVANCED">
 *   <AdvancedReportButton />
 * </ModuleGate>
 *
 * @example
 * // Show upgrade prompt if module not enabled
 * <ModuleGate
 *   requiredModule="LOYALTY_PROGRAM"
 *   fallback={<UpgradePrompt module="Loyalty Program" />}
 * >
 *   <LoyaltySettings />
 * </ModuleGate>
 *
 * @example
 * // Show loading state while modules are being fetched
 * <ModuleGate
 *   requiredModule="BATCH_TRACKING"
 *   loading={<Skeleton />}
 *   fallback={<UpgradePrompt />}
 * >
 *   <BatchTrackingPanel />
 * </ModuleGate>
 */
export function ModuleGate({
  requiredModule,
  children,
  fallback = null,
  loading = null,
}: ModuleGateProps) {
  const user = useAuthStore((state) => state.user);
  const enabledModules = user?.enabledModules;

  // Modules not loaded yet - show loading state
  if (!enabledModules) {
    return <>{loading}</>;
  }

  // Check if user has access to the required module
  const hasAccess = enabledModules.includes(requiredModule);

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

/**
 * Hook to check module access imperatively
 */
export function useModuleAccess() {
  const user = useAuthStore((state) => state.user);
  const enabledModules = user?.enabledModules;

  return {
    /**
     * Check if user has access to a specific module
     */
    hasModule: (moduleCode: string): boolean => {
      if (!enabledModules) return false;
      return enabledModules.includes(moduleCode);
    },

    /**
     * Check if user has access to any of the specified modules
     */
    hasAnyModule: (moduleCodes: string[]): boolean => {
      if (!enabledModules) return false;
      return moduleCodes.some((code) => enabledModules.includes(code));
    },

    /**
     * Check if user has access to all of the specified modules
     */
    hasAllModules: (moduleCodes: string[]): boolean => {
      if (!enabledModules) return false;
      return moduleCodes.every((code) => enabledModules.includes(code));
    },

    /**
     * Get list of enabled modules
     */
    enabledModules: enabledModules ?? [],

    /**
     * Check if modules are still loading
     */
    isLoading: !enabledModules,
  };
}
